import { useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { HoverCard, HoverCardContent, HoverCardPortal, HoverCardTrigger } from '@artstorefronts/ui';
import { RepSparkIcon } from './ReputationIcons';
import { ReputationPopover } from './ReputationPopover';
import { useEarnChoreography } from './useEarnChoreography';
import { tweenNumber, formatBalance, prefersReducedMotion } from './tween-number';
import { MY_REPUTATION_BALANCE_QUERY, type ReputationBalance } from './reputation-queries';
import { buildTownSquareUrl } from '../../utils';
import './reputation.css';

const POLL_MS = 15000;
const REDUCED_TWEEN_MS = 250;
const INCREASE_TWEEN_MS = 600;
const DECREASE_TWEEN_MS = 350;

interface PillData {
  myReputationBalance: ReputationBalance;
}

function getSoundOn(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem('rep:soundOn') === '1';
  } catch {
    return false;
  }
}

export function ReputationPill() {
  const { t } = useTranslation('reputation');
  const pillRef = useRef<HTMLButtonElement | null>(null);
  const numRef = useRef<HTMLSpanElement | null>(null);
  const cancelTweenRef = useRef<(() => void) | null>(null);

  const hasMountedOnce = useRef(false);
  const displayedBalanceRef = useRef(0);

  const { data, stopPolling, startPolling } = useQuery<PillData>(MY_REPUTATION_BALANCE_QUERY, {
    pollInterval: POLL_MS,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
    errorPolicy: 'all',
  });

  const { celebrate } = useEarnChoreography(pillRef);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const syncPolling = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        startPolling(POLL_MS);
      }
    };
    syncPolling();
    document.addEventListener('visibilitychange', syncPolling);
    return () => {
      document.removeEventListener('visibilitychange', syncPolling);
    };
  }, [startPolling, stopPolling]);

  const balanceResult = data?.myReputationBalance;
  /**
   * The pill shows SPENDABLE points, matching the headline on /points and the town-square copy
   * of this component. An earn raises it (and still celebrates); a redemption lowers it, which
   * is the point. Reputation lives on in `balance`, which spending never touches.
   */
  const spendablePoints = balanceResult?.spendablePoints ?? 0;

  useEffect(() => {
    if (!balanceResult) return;
    const numEl = numRef.current;
    if (!numEl) {
      if (!hasMountedOnce.current) {
        displayedBalanceRef.current = spendablePoints;
        hasMountedOnce.current = true;
      }
      return;
    }

    if (cancelTweenRef.current) {
      cancelTweenRef.current();
      cancelTweenRef.current = null;
    }

    if (!hasMountedOnce.current) {
      numEl.textContent = formatBalance(spendablePoints);
      displayedBalanceRef.current = spendablePoints;
      hasMountedOnce.current = true;
      return;
    }

    const previous = displayedBalanceRef.current;
    const delta = spendablePoints - previous;
    if (delta === 0) return;

    const reduced = prefersReducedMotion();
    const duration = delta > 0 ? (reduced ? REDUCED_TWEEN_MS : INCREASE_TWEEN_MS) : DECREASE_TWEEN_MS;
    cancelTweenRef.current = tweenNumber(numEl, previous, spendablePoints, duration, formatBalance);
    displayedBalanceRef.current = spendablePoints;

    if (delta > 0) {
      celebrate(delta, { soundOn: getSoundOn() });
    }
  }, [spendablePoints, balanceResult, celebrate]);

  useEffect(() => {
    return () => {
      if (cancelTweenRef.current) cancelTweenRef.current();
    };
  }, []);

  const handleClick = () => {
    window.location.href = buildTownSquareUrl('/points');
  };

  if (!balanceResult) return null;

  return (
    <HoverCard openDelay={150} closeDelay={150}>
      <HoverCardTrigger asChild>
        <button
          ref={pillRef}
          type="button"
          className="rep-pill"
          aria-label={t('pill.aria-label', { balance: spendablePoints })}
          onClick={handleClick}
        >
          <RepSparkIcon className="rep-spark" />
          <span className="rep-num" ref={numRef}>
            {formatBalance(spendablePoints)}
          </span>
        </button>
      </HoverCardTrigger>
      <HoverCardPortal>
        <HoverCardContent
          side="bottom"
          sideOffset={8}
          align="center"
          className="p-0 border-0 shadow-none bg-transparent"
        >
          <ReputationPopover balance={spendablePoints} headingKey="your" />
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
}
